import type { Vehicle } from '@features/vehiclesearch/types/vinvrmsearch.types';
import type { VehicleDerivative } from '@features/vehiclesearch/types/manualsearch.types';

export interface VehicleScheduleContext {
    vehicleAgeYears?: number;
    firstRegistrationDate?: string;
    vehicleMileage?: number;
    vehicleFuelType?: string;
    annualMileage?: number;
    lastServiceDate?: string;
    lastServiceMileage?: number;
    // Identity dimensions (11.3.2): PlanResolver uses these to discard non-matching rules.
    makeId?: string;
    modelId?: string;
    derivativeId?: string;
    transmissionId?: string;
    fuelTypeId?: string;
    // integer cc >= 100; omitted when absent, non-integer (legacy litres), or below the floor
    engineSize?: number;
    // Matcher-only: set only when the derivative reports it, never emitted as false.
    is4x4?: boolean;
}

// Same string rule as features/quote/utils/dateKey.ts toDateKey: leading
// calendar date, no Date construction, so no timezone shift.
const dateKeyOf = (value: string): string | undefined =>
    /^(\d{4}-\d{2}-\d{2})/.exec(value.trim())?.[1];

const MIN_PLAUSIBLE_ENGINE_CC = 100;

/**
 * Derives the age/mileage/fuel-type/annual-mileage/last-service-date dimensions
 * shared by product search eligibility (`useProductSearchWithQuery`) and the
 * derived-schedule query (`useDerivedSchedule`, Epic 2.4.2 Slice B), from
 * either a full `Vehicle` (with ownership history in `current_status`) or a
 * bare `VehicleDerivative` (manual-search path, no ownership history).
 *
 * Extracted from `useProductSearchWithQuery.ts` (the original vehicle
 * eligibility derivation, PR #402 lineage) so both call sites derive
 * identical dimensions from identical vehicle shapes instead of maintaining
 * two copies of this branching that could silently drift apart.
 */
export const deriveVehicleScheduleContext = (
    vehicle: Vehicle | VehicleDerivative | null | undefined
): VehicleScheduleContext => {
    if (!vehicle) {
        return {};
    }

    const result: VehicleScheduleContext = {};

    if ('date_of_first_registration' in vehicle && vehicle.date_of_first_registration) {
        const regDate = new Date(vehicle.date_of_first_registration);
        const ageMs = Date.now() - regDate.getTime();
        const ageYears = Math.floor(ageMs / (1000 * 60 * 60 * 24 * 365.25));
        if (ageYears >= 0) {
            result.vehicleAgeYears = ageYears;
        }
        const registrationKey = dateKeyOf(vehicle.date_of_first_registration);
        if (registrationKey) result.firstRegistrationDate = registrationKey;
    } else if ('first_year_of_manufacture' in vehicle && vehicle.first_year_of_manufacture) {
        const ageYears = new Date().getFullYear() - Number(vehicle.first_year_of_manufacture);
        if (ageYears >= 0) {
            result.vehicleAgeYears = ageYears;
        }
    }

    if ('current_status' in vehicle && vehicle.current_status?.mileage != null) {
        result.vehicleMileage = vehicle.current_status.mileage;
    }

    if ('current_status' in vehicle && vehicle.current_status?.annual_mileage != null) {
        result.annualMileage = vehicle.current_status.annual_mileage;
    }

    // Issue #1233 — an incomplete pair is sent as no history (PRD Decision 12) so the
    // derived-schedule query cannot 422 against the backend's both-or-neither rule.
    if (
        'last_service_date' in vehicle &&
        vehicle.last_service_date &&
        typeof vehicle.last_service_mileage === 'number'
    ) {
        result.lastServiceDate = vehicle.last_service_date;
        result.lastServiceMileage = vehicle.last_service_mileage;
    }

    const derivative =
        'derivative' in vehicle && vehicle.derivative ? vehicle.derivative : vehicle;

    if ('fuel_type' in derivative && derivative.fuel_type?.code) {
        result.vehicleFuelType = derivative.fuel_type.code;
    }

    if ('make' in derivative && 'model' in derivative) {
        if (derivative.make?.id) result.makeId = derivative.make.id;
        if (derivative.model?.id) result.modelId = derivative.model.id;
        if (derivative.id) result.derivativeId = derivative.id;
        if (derivative.transmission?.id) result.transmissionId = derivative.transmission.id;
        if (derivative.fuel_type?.id) result.fuelTypeId = derivative.fuel_type.id;
        if (derivative.is_4x4) result.is4x4 = true;
    }

    if ('engine_size' in derivative && derivative.engine_size?.size != null) {
        const engineSize = Number(derivative.engine_size.size);
        if (Number.isInteger(engineSize) && engineSize >= MIN_PLAUSIBLE_ENGINE_CC) {
            result.engineSize = engineSize;
        }
    }

    return result;
};
